import React from 'react';
import {
  format as formatDateFns,
  formatRelative as formatRelativeDateFns,
  isValid as isDateValid,
  formatDistance,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  parseISO,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Translation } from 'react-i18next';
import { i18n, dateFnsLocales } from '@pypestream/translations';

enum DateFormats {
  // Need to fix localization for `at` key word.
  'default' = "MM/dd/yyyy 'at' hh:mm aa",
}

export { isDateValid };

export const format = (
  date: Date | number,
  dateFormat: DateFormats = DateFormats.default,
  options?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  }
): JSX.Element => {
  return (
    <Translation>
      {() =>
        formatDateFns(date, dateFormat, {
          ...options,
          ...(!options?.locale && {
            locale: dateFnsLocales[i18n.language],
          }),
        })
      }
    </Translation>
  );
};

export const formatRelative = (
  date: Date | number,
  baseDate: Date | number,
  options?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  }
): JSX.Element => {
  return (
    <Translation>
      {() =>
        formatRelativeDateFns(date, baseDate, {
          ...options,
          ...(!options?.locale && {
            locale: dateFnsLocales[i18n.language],
          }),
        })
      }
    </Translation>
  );
};

export const getDateOrNull = (value: Date | string | null): Date | null => {
  if (value !== null && isDateValid(new Date(value))) {
    return new Date(value);
  }

  return null;
};

export type FormatType = 'strict' | 'toNow' | 'distance';

interface FormatTimeOptions {
  fromDate?: string;
  toDate?: string; // Only for `formatDistance`.
  locale?: Locale;
  formatType?: FormatType;
  /**
   * @deprecated No-op since PSTUDIO-2614 — relative-distance formatting is
   * timezone-invariant, so this value has no effect on the output. Kept only
   * for backward source-compatibility with existing call sites.
   */
  userTimeZone?: string;
  addSuffix?: boolean;
  unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year'; // Only for `formatDistanceToNowStrict`.
  roundingMethod?: 'floor' | 'ceil' | 'round'; // Only for `formatDistanceToNowStrict`.
}

export const formatTimeWithTimezone = (options: FormatTimeOptions): string => {
  try {
    const {
      fromDate,
      toDate,
      locale,
      formatType = 'toNow',
      addSuffix = true,
      unit,
      roundingMethod,
    } = options;

    if (!fromDate) {
      throw new Error('fromDate is required');
    }

    if (formatType === 'distance' && !toDate) {
      throw new Error('Parameter toDate is required for formatDistance method');
    }

    if (formatType !== 'distance' && toDate) {
      throw new Error(
        'Parameter toDate is required only for formatDistance method'
      );
    }

    if (formatType !== 'strict' && (unit || roundingMethod)) {
      throw new Error(
        'Parameter unit or roundingMethod is required only for formatDistanceToNowStrict method'
      );
    }

    // Relative-distance formatting is timezone-invariant: the elapsed time
    // between two instants does not depend on the zone they are displayed in.
    // Previously each instant was passed through `toZonedTime`, which shifts the
    // instant by the zone offset; because `formatDistanceToNow`/`Strict` compare
    // against the *real* (unshifted) current instant, a user timezone differing
    // from the browser produced an offset-sized error — a 3-minute-old event
    // rendered as "3 hours" (PSTUDIO-2614). Compare the raw instants instead.
    const parsedFromDate = parseISO(fromDate);
    const parsedToDate = parseISO(toDate || new Date().toISOString());

    switch (formatType) {
      case 'strict':
        return formatDistanceToNowStrict(parsedFromDate, {
          addSuffix,
          unit,
          roundingMethod,
          locale,
        });

      case 'distance':
        return formatDistance(parsedFromDate, parsedToDate, {
          addSuffix,
          locale,
        });

      case 'toNow':
      default:
        return formatDistanceToNow(parsedFromDate, {
          addSuffix,
          locale,
        });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date format';
  }
};

interface FormatAbsoluteTimeOptions {
  fromDate?: string;
  locale?: Locale;
  /**
   * IANA timezone identifier (e.g. "America/New_York"). Defaults to the
   * runtime/browser zone when omitted.
   */
  timeZone?: string;
}

// "Jul 2, 2026, 08:15 AM GMT+2" — 12-hour clock + short GMT offset.
const ABSOLUTE_DATE_TIME_FORMAT = 'MMM d, yyyy, hh:mm aa O';

/**
 * Absolute wall-clock timestamp with an explicit GMT offset, rendered in a
 * specific IANA zone. Unlike `formatTimeWithTimezone` (which is relative —
 * "5 minutes ago"), this shows the exact instant, used by Manager Audit Logs
 * so coordinators can read precise login/logout times (PSTUDIO-3416).
 */
export const formatAbsoluteTimeWithTimezone = (
  options: FormatAbsoluteTimeOptions
): string => {
  try {
    const { fromDate, locale, timeZone } = options;

    if (!fromDate) {
      throw new Error('fromDate is required');
    }

    const parsedFromDate = parseISO(fromDate);

    if (!isDateValid(parsedFromDate)) {
      throw new Error('Invalid date');
    }

    const zone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    return formatInTimeZone(parsedFromDate, zone, ABSOLUTE_DATE_TIME_FORMAT, {
      locale,
    });
  } catch (error) {
    console.error('Error formatting absolute date:', error);
    return 'Invalid date format';
  }
};
